import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { AuthErrorBoundary } from "@/components/AuthErrorBoundary";
import { RequireAccountRole } from "@/components/auth/RequireAccountRole";
import { useAuthErrorHandler } from "@/hooks/useAuthErrorHandler";
import { PrivateMessageNotificationsProvider } from "@/contexts/PrivateMessageNotificationsContext";
import { AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction } from "@/components/ui/alert-dialog";
import PageLoader from "./components/shared/PageLoader";
import { toast } from "@/hooks/use-toast";
import { generalReportException } from "@/lib/logs/reportExceptions";
import { env } from "@/lib/env";

// Lazy loaded page components
const CustomerIndex = lazy(() => import("./pages/customer/Index"));
const Auth = lazy(() => import("./pages/auth/Auth"));
const ExpertAuth = lazy(() => import("./pages/auth/ExpertAuth"));
const AdminAuth = lazy(() => import("./pages/auth/AdminAuth"));
const CustomerAuth = lazy(() => import("./pages/auth/CustomerAuth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdminApplicationDetail = lazy(() => import("./pages/admin/AdminApplicationDetail"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const ExpertIndex = lazy(() => import("./pages/expert/Index"));
const ExpertOnboarding = lazy(() => import("./pages/expert/ExpertOnboarding"));
const PaymentReturn = lazy(() => import("./pages/payment/PaymentReturn"));

// Prevent white loading screen on tab focus
const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Hook'ta kendi onError'u varsa, global handler toast gostermesin (duplikasyon onlenir)
      if (mutation.options.onError) return;

      generalReportException(error as Error, {
        operation: "global.mutation.onError",
        additionalData: { mutationKey: mutation.options.mutationKey },
      });
      toast({
        title: "Bir hata olustu",
        description: (error as Error).message || "Islem sirasinda beklenmeyen bir hata meydana geldi.",
        variant: "destructive",
      });
    },
  }),
});

function LogoutModal() {
  const { showLogoutModal, setShowLogoutModal, logoutReason } = useAuth();
  const navigate = useNavigate();

  const handleLoginRedirect = () => {
    setShowLogoutModal(false);
    navigate("/uye-ol");
  };

  return (
    <AlertDialog open={showLogoutModal} onOpenChange={setShowLogoutModal}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Oturumunuz Sonlandı</AlertDialogTitle>
          <AlertDialogDescription>
            {logoutReason}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleLoginRedirect}>
            Tekrar Giriş Yap
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AppContent() {
  useAuthErrorHandler(); // Initialize global auth error handling

  return (
    <>
      <LogoutModal />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Auth />} />
          <Route path="/uye-ol" element={<Auth />} />
          <Route path="/expert-auth" element={<ExpertAuth />} />
          <Route path="/uzman-girisi" element={<ExpertAuth />} />
          <Route path="/musteri-auth" element={<CustomerAuth />} />
          <Route path="/isveren-girisi" element={<CustomerAuth />} />
          <Route path="/admin_page" element={<AdminAuth />} />
          {env.VITE_APP_ENV === "staging" && <Route path="/payment/success" element={<PaymentReturn outcome="success" />} />}
          {env.VITE_APP_ENV === "staging" && <Route path="/payment/failure" element={<PaymentReturn outcome="failure" />} />}
          <Route path="/admin/applications/:id" element={<AdminApplicationDetail />} />
          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
          <Route path="/admin/:tab?" element={<AdminDashboard />} />
          <Route
            path="/expert-onboarding"
            element={(
              <RequireAccountRole
                role="expert"
                redirectTo="/uzman-girisi"
                loadingFallback={<PageLoader />}
              >
                <ExpertOnboarding />
              </RequireAccountRole>
            )}
          />
          <Route path="/expert/*" element={<ExpertIndex />} />
          <Route path="/customer/*" element={<CustomerIndex />} />
          <Route path="/404" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange
    >
      <AuthProvider>
        <BrowserRouter>
          <PrivateMessageNotificationsProvider>
            <AuthErrorBoundary>
              <TooltipProvider>
                <Sonner />
                <AppContent />
              </TooltipProvider>
            </AuthErrorBoundary>
          </PrivateMessageNotificationsProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
